import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, Badge, HStack } from "@chakra-ui/react";
import { FaChartLine, FaMobileAlt, FaBolt, FaCloudSun } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const GOLD = "#C69214";
const AMBER = "#F2B705";

const SolarServicesMonitoring: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/services/monitoring"
        fallbackTitle="Solar Monitoring Services"
        fallbackDescription="Real-time monitoring and optimization of your solar system performance"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaChartLine} boxSize={16} color={AMBER} />
            <Heading size="2xl" fontWeight="bold">
              Smart Solar Monitoring
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Real-time insights into your solar system's performance
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Monitoring Features */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center">Monitoring Features</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="2xl">
            Track every aspect of your solar system in real-time
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Box bg="white" p={6} borderRadius="lg" textAlign="center" border="2px solid" borderColor={GOLD}>
            <Icon as={FaBolt} boxSize={10} color={GOLD} mb={3} />
            <Heading size="sm" mb={2}>Live Production</Heading>
            <Text fontSize="sm" color="gray.600">
              Watch your panels generate power in real-time
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" textAlign="center" border="2px solid" borderColor={GOLD}>
            <Icon as={FaChartLine} boxSize={10} color={GOLD} mb={3} />
            <Heading size="sm" mb={2}>Performance Analytics</Heading>
            <Text fontSize="sm" color="gray.600">
              Historical data and trend analysis
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" textAlign="center" border="2px solid" borderColor={GOLD}>
            <Icon as={FaMobileAlt} boxSize={10} color={GOLD} mb={3} />
            <Heading size="sm" mb={2}>Mobile App Access</Heading>
            <Text fontSize="sm" color="gray.600">
              Monitor from anywhere via smartphone
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" textAlign="center" border="2px solid" borderColor={AMBER}>
            <Icon as={FaCloudSun} boxSize={10} color={AMBER} mb={3} />
            <Heading size="sm" mb={2}>Weather Integration</Heading>
            <Text fontSize="sm" color="gray.600">
              Correlate production with weather data
            </Text>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Mobile App */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Badge bg={GOLD} color="white" fontSize="sm" px={3} py={1} borderRadius="full">MOBILE APP</Badge>
              <Heading size="xl">Monitor From Your Phone</Heading>
              <Text fontSize="lg" color="gray.600">
                Our intuitive mobile app gives you complete visibility into your solar system's performance, wherever you are.
              </Text>
              <VStack align="flex-start" spacing={3} w="full">
                <HStack>
                  <Icon as={FaBolt} color={GOLD} />
                  <Text fontWeight="medium">Real-time energy production</Text>
                </HStack>
                <HStack>
                  <Icon as={FaChartLine} color={GOLD} />
                  <Text fontWeight="medium">Daily, weekly, monthly reports</Text>
                </HStack>
                <HStack>
                  <Icon as={FaMobileAlt} color={GOLD} />
                  <Text fontWeight="medium">Push notifications for alerts</Text>
                </HStack>
              </VStack>
            </VStack>

            <Box
              h={400}
              bg="white"
              borderRadius="2xl"
              shadow="2xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="4px solid"
              borderColor={GOLD}
            >
              <VStack spacing={4}>
                <Icon as={FaMobileAlt} boxSize={20} color={AMBER} />
                <Text color="gray.500" fontSize="sm" fontWeight="medium">
                  App Screenshot Placeholder
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Live Dashboard */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center">Live Dashboard Preview</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="2xl">
            See what you'll monitor every day
          </Text>
        </VStack>

        <Box
          bg="rgba(198, 146, 20, 0.06)"
          p={8}
          borderRadius="xl"
          border="2px solid"
          borderColor={GOLD}
        >
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box bg="white" p={6} borderRadius="lg" shadow="md">
              <Text fontSize="sm" color="gray.500" mb={2}>Current Production</Text>
              <Heading size="2xl" color={GOLD}>3.2 kW</Heading>
              <Text fontSize="sm" color="gray.600" mt={2}>Generating now</Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" shadow="md">
              <Text fontSize="sm" color="gray.500" mb={2}>Today's Energy</Text>
              <Heading size="2xl" color={AMBER}>28.5 kWh</Heading>
              <Text fontSize="sm" color="gray.600" mt={2}>Total produced</Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" shadow="md">
              <Text fontSize="sm" color="gray.500" mb={2}>Today's Savings</Text>
              <Heading size="2xl" color={GOLD}>$8.55</Heading>
              <Text fontSize="sm" color="gray.600" mt={2}>Grid cost avoided</Text>
            </Box>
          </SimpleGrid>

          <Box mt={6} p={6} bg="white" borderRadius="lg">
            <Text fontSize="sm" color="gray.500" mb={4}>Production Graph</Text>
            <Box
              h={150}
              bg="gray.50"
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="2px dashed"
              borderColor="gray.300"
            >
              <Text color="gray.400" fontSize="sm">Interactive Chart Placeholder</Text>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* CTA */}
      <Box bg={NAVY} py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center" color="white">
            <Heading size="xl">Start Monitoring Your Solar System</Heading>
            <Text fontSize="lg" opacity={0.9} maxW="2xl">
              All our systems include advanced monitoring at no extra cost
            </Text>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarServicesMonitoring;
